<script setup lang="ts">
import {Dialog, DialogHeader, DialogScrollContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import {computed} from "vue";
import LaboratoryPartnershipType, {
    LaboratoryPartnershipTypeSlug
} from "@/core/interfaces/laboratory/LaboratoryPartnershipType";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Label} from "@/core/components/ui/label";
import InformationTooltip from "@/core/components/tooltips/InformationTooltip.vue";
import {ChevronRight} from "@lucide/vue";
import {LaboratoryDisruptionAlert} from "@/core/interfaces/laboratory/Laboratory";

const props = defineProps<{
    partnershipTypes?: LaboratoryPartnershipType[];
    alert?: LaboratoryDisruptionAlert;
    partnerName?: string;
    branding?: {
        logo?: {
            renderPath: string;
        };
    };
}>();

/**
 * Determine if the organization is a partner for product availability notifications.
 */
const isAvailabilityAgainPartner = computed(() => {
    return props.partnershipTypes
        ?.some((type) => type.slug === LaboratoryPartnershipTypeSlug.PRODUCT_AVAILABILITY) ?? false;
});

/**
 * Determine if the organization has disruption alert enabled.
 */
const hasDisruptionAlert = computed(() => {
    return !!props.alert?.is_enabled;
});
</script>

<template>
  <Dialog>
    <DialogTrigger as-child>
      <slot>
        <div
          class="border-[1.5px] p-4 border-primary rounded-lg bg-white w-full md:w-[260px]"
        >
          <div class="flex gap-4 items-center">
            <img
              v-if="branding?.logo"
              alt="logo"
              :src="branding.logo.renderPath"
              class="h-12 w-12 object-contain"
            >
            <p class="text-primary flex-1 line-clamp-2">
              Découvrir les outils {{ partnerName }}
            </p>
            <ChevronRight class="text-gray-300 shrink-0 w-6" />
          </div>
        </div>
      </slot>
    </DialogTrigger>
    <DialogScrollContent class="space-y-4">
      <DialogHeader>
        <DialogTitle>
          {{ partnerName ?? "Laboratoire partenaire" }}
        </DialogTitle>
      </DialogHeader>
      <img
        v-if="branding?.logo?.renderPath"
        alt="logo"
        :src="branding?.logo?.renderPath"
      >
      <div class="space-y-2 bg-gray-100 rounded-md p-2">
        <div class="flex items-center gap-x-2">
          <Checkbox id="availability" :model-value="isAvailabilityAgainPartner" />
          <Label for="availability" class="flex items-center gap-1 relative">
            Retours de disponibilité
            <InformationTooltip v-if="isAvailabilityAgainPartner" class="w-4 h-4 text-primary">
              <div class="text-base max-w-md">
                Vous recevrez une notification sur les postes Connect lorsqu’un produit en rupture sera à nouveau
                disponible
              </div>
            </InformationTooltip>
          </Label>
        </div>
        <div class="flex items-center gap-x-2">
          <Checkbox id="disruption-alert" :model-value="hasDisruptionAlert" />
          <Label for="disruption-alert" class="flex items-center gap-1 relative">
            Offre de dépannage
            <InformationTooltip class="w-4 h-4 text-primary -top-0.5">
              <div class="text-base max-w-md">
                Vous recevrez une offre de dépannage sur les postes Connect lorsque le laboratoire est en mesure de
                répondre à une rupture dans votre agence
              </div>
            </InformationTooltip>
          </Label>
        </div>
      </div>
      <div v-if="alert?.presentation">
        <p v-html="alert.presentation" />
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>

</style>
